import { create } from "zustand";
import {
  persist,
  createJSONStorage,
  StateStorage,
  devtools,
} from "zustand/middleware";
import { useCallback } from "react";
import { useStorageSync } from "hooks/useSyncTabsEffect";

import { SelectedProduct } from "routes/guide-details/guide-details";

export interface GuideFormData {
  description: string;
  selectedDevices: SelectedProduct[];
  video: File | null;
  currentStep: number;
}

export interface DeviceOption {
  value: string;
  label: string;
}

interface NewGuideFormState {
  // State
  formData: GuideFormData;

  // Actions
  setFormData: (newFormData: Partial<GuideFormData>) => void;
  clearFormData: () => void;
  setDescription: (description: string) => void;
  setSelectedDevices: (devices: SelectedProduct[]) => void;
  setVideo: (video: File | null) => void;
  setCurrentStep: (step: number) => void;
  nextStep: () => void;
  previousStep: () => void;
}

const initialFormData: GuideFormData = {
  description: "",
  selectedDevices: [],
  video: null,
  currentStep: 0,
};

// Create a custom storage object
const customStorage: StateStorage = {
  getItem: (name) => {
    const str = localStorage.getItem(name);
    return str;
  },
  setItem: (name, value) => {
    // Don't persist File objects as they can't be serialized
    const parsed = JSON.parse(value);
    if (parsed.state?.formData?.video) {
      parsed.state.formData.video = null;
    }
    localStorage.setItem(name, JSON.stringify(parsed));
  },
  removeItem: (name) => localStorage.removeItem(name),
};

// Storage key for localStorage events
const STORAGE_KEY = "new-guide-form";

// DevTools configuration options
const devToolsOptions = {
  name: "NewGuideForm",
  enabled: true,
  anonymousActionType: "UNKNOWN_ACTION",
  serialize: {
    options: true,
  },
};

// Create the store with middleware chain
export const useNewGuideFormStore = create<NewGuideFormState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        formData: initialFormData,

        // Actions
        setFormData: (newFormData) =>
          set(
            (state) => ({
              formData: { ...state.formData, ...newFormData },
            }),
            false,
            { type: "new-guide-form/setFormData", payload: newFormData }
          ),

        clearFormData: () =>
          set(
            { formData: initialFormData },
            false,
            "new-guide-form/clearFormData"
          ),

        setDescription: (description) =>
          set(
            (state) => ({
              formData: { ...state.formData, description },
            }),
            false,
            {
              type: "new-guide-form/setDescription",
              payload: description,
            }
          ),

        setSelectedDevices: (selectedDevices) =>
          set(
            (state) => ({
              formData: { ...state.formData, selectedDevices },
            }),
            false,
            {
              type: "new-guide-form/setSelectedDevices",
              payload: selectedDevices.map((d) => ({ id: d.id, name: d.name })),
            }
          ),

        setVideo: (video) =>
          set(
            (state) => ({
              formData: { ...state.formData, video },
            }),
            false,
            {
              type: "new-guide-form/setVideo",
              payload: video ? video.name : null, // Only log filename for debugging
            }
          ),

        setCurrentStep: (currentStep) =>
          set(
            (state) => ({
              formData: { ...state.formData, currentStep },
            }),
            false,
            {
              type: "new-guide-form/setCurrentStep",
              payload: currentStep,
            }
          ),

        nextStep: () => {
          const { formData } = get();
          const maxSteps = 2; // Adjust based on your form steps
          if (formData.currentStep < maxSteps - 1) {
            set(
              (state) => ({
                formData: {
                  ...state.formData,
                  currentStep: state.formData.currentStep + 1,
                },
              }),
              false,
              "new-guide-form/nextStep"
            );
          }
        },

        previousStep: () => {
          const { formData } = get();
          if (formData.currentStep > 0) {
            set(
              (state) => ({
                formData: {
                  ...state.formData,
                  currentStep: state.formData.currentStep - 1,
                },
              }),
              false,
              "new-guide-form/previousStep"
            );
          }
        },
      }),
      {
        name: STORAGE_KEY,
        // Select which part of the state to persist (exclude video file)
        partialize: (state) => ({
          formData: {
            ...state.formData,
            video: null, // Don't persist video files
          },
        }),
        storage: createJSONStorage(() => customStorage),
      }
    ),
    devToolsOptions
  )
);

// Hook for synchronizing state between tabs
export function useSyncTabsEffect() {
  const setFormData = useNewGuideFormStore((state) => state.setFormData);

  const handleChange = useCallback(
    (newValue: string | null) => {
      if (!newValue) return;
      try {
        const newData = JSON.parse(newValue);
        if (newData?.state?.formData) {
          // Don't sync video file between tabs
          const { video, ...syncableData } = newData.state.formData;
          setFormData(syncableData);
        }
      } catch (error) {
        console.error("Error synchronizing tabs:", error);
      }
    },
    [setFormData]
  );

  useStorageSync(STORAGE_KEY, handleChange);
}
